// Campos que se pueden importar, cómo reconocer las columnas del archivo y cómo normalizar cada valor.

export const IMPORT_FIELDS = [
  { key: 'plot', label: 'Nave', required: true, synonyms: ['nave', 'codigo', 'codigo nave', 'clave', 'unidad', 'bodega', 'local', 'nombre nave', 'plot'] },
  { key: 'tenant', label: 'Inquilino', required: true, synonyms: ['inquilino', 'arrendatario', 'cliente', 'empresa', 'razon social', 'nombre inquilino', 'tenant'] },
  { key: 'startDate', label: 'Fecha de inicio', required: true, synonyms: ['inicio', 'fecha inicio', 'fecha de inicio', 'inicio contrato', 'inicio de contrato', 'vigencia desde', 'desde', 'start'] },
  { key: 'endDate', label: 'Fecha de vencimiento', required: true, synonyms: ['fin', 'vencimiento', 'fecha fin', 'fecha de fin', 'fecha de vencimiento', 'termino', 'fin contrato', 'vigencia hasta', 'hasta', 'end'] },
  { key: 'rent', label: 'Renta mensual', required: true, synonyms: ['renta', 'renta mensual', 'mensualidad', 'monto', 'importe', 'rent'] },
  { key: 'rfc', label: 'RFC', synonyms: ['rfc'] },
  { key: 'email', label: 'Correo de cobranza', synonyms: ['correo', 'email', 'e-mail', 'mail', 'correo cobranza', 'correo de cobranza', 'correo electronico'] },
  { key: 'phone', label: 'Teléfono', synonyms: ['telefono', 'tel', 'celular', 'movil'] },
  { key: 'contactName', label: 'Contacto de pagos', synonyms: ['contacto', 'nombre contacto', 'contacto de pagos', 'contacto cobranza'] },
  { key: 'paymentDay', label: 'Día de pago', synonyms: ['dia de pago', 'dia pago', 'dia de cobro', 'dia limite'] },
  { key: 'deposit', label: 'Depósito en garantía', synonyms: ['deposito', 'deposito en garantia', 'garantia', 'fianza'] },
  { key: 'escalationPercent', label: 'Escalación anual (%)', synonyms: ['escalacion', 'escalacion anual', 'incremento', 'incremento anual', 'aumento anual', '% anual', 'incremento %'] },
  { key: 'openingBalance', label: 'Saldo anterior a Parkos', synonyms: ['saldo', 'saldo anterior', 'adeudo', 'saldo pendiente', 'saldo vencido', 'deuda'] },
]

const norm = (s) => String(s || '')
  .toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9% ]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()

// Propone qué columna del archivo corresponde a cada campo (índice de columna o -1).
export function autoMap(headers) {
  const normalized = headers.map(norm)
  const taken = new Set()
  const mapping = {}
  const find = (field) => {
    let idx = normalized.findIndex((h, i) => !taken.has(i) && field.synonyms.includes(h))
    if (idx < 0) idx = normalized.findIndex((h, i) => !taken.has(i) && h && field.synonyms.some((s) => s.length > 3 && (h.includes(s) || s.includes(h))))
    return idx
  }
  for (const field of IMPORT_FIELDS) {
    const idx = find(field)
    mapping[field.key] = idx
    if (idx >= 0) taken.add(idx)
  }
  return mapping
}

const MONTHS = { ene: 1, feb: 2, mar: 3, abr: 4, may: 5, jun: 6, jul: 7, ago: 8, sep: 9, set: 9, oct: 10, nov: 11, dic: 12 }
const pad = (n) => String(n).padStart(2, '0')
const validYmd = (y, m, d) => {
  const dt = new Date(Date.UTC(y, m - 1, d))
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
}

// Fechas a AAAA-MM-DD. Acepta ISO, día/mes/año (México), "15 ene 2025" y números de serie de Excel.
export function toYmd(value) {
  const v = String(value ?? '').trim()
  if (!v) return ''
  if (/^\d{5}(\.\d+)?$/.test(v)) {
    const n = Math.floor(Number(v))
    if (n >= 20000 && n <= 80000) return new Date(Date.UTC(1899, 11, 30) + n * 86400000).toISOString().slice(0, 10)
  }
  let m = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (m && validYmd(+m[1], +m[2], +m[3])) return `${m[1]}-${pad(m[2])}-${pad(m[3])}`
  m = v.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2}|\d{4})$/)
  if (m) {
    const y = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])
    if (validYmd(y, +m[2], +m[1])) return `${y}-${pad(m[2])}-${pad(m[1])}`
  }
  m = norm(v).match(/^(\d{1,2}) (?:de )?([a-z]{3})[a-z]* (?:de )?(\d{4})$/)
  if (m && MONTHS[m[2]] && validYmd(+m[3], MONTHS[m[2]], +m[1])) return `${m[3]}-${pad(MONTHS[m[2]])}-${pad(m[1])}`
  return v
}

// Montos: quita $, comas y espacios. Si no es número, se deja tal cual para que el servidor lo marque.
export function toAmount(value) {
  const v = String(value ?? '').replace(/[$%\s]/g, '').replace(/,/g, '')
  if (v === '') return ''
  const n = Number(v)
  return Number.isFinite(n) ? n : String(value).trim()
}

// Convierte la tabla del archivo en las filas que recibe el servidor, según el mapeo elegido.
export function buildRows(table, mapping, hasHeader) {
  const body = hasHeader ? table.slice(1) : table
  return body.map((cols) => {
    const get = (key) => (mapping[key] >= 0 ? String(cols[mapping[key]] ?? '').trim() : '')
    return {
      plot: get('plot'),
      tenant: get('tenant'),
      rfc: get('rfc'),
      email: get('email'),
      phone: get('phone'),
      contactName: get('contactName'),
      startDate: toYmd(get('startDate')),
      endDate: toYmd(get('endDate')),
      rent: toAmount(get('rent')),
      paymentDay: toAmount(get('paymentDay')),
      deposit: toAmount(get('deposit')),
      escalationPercent: toAmount(get('escalationPercent')),
      openingBalance: toAmount(get('openingBalance')),
    }
  })
}

const csvCell = (v) => (/[",\n;]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v))

// Plantilla para descargar: encabezados reconocidos y dos filas de ejemplo. Lleva BOM para que Excel respete los acentos.
export function templateCsv() {
  const headers = ['Nave', 'Inquilino', 'RFC', 'Correo de cobranza', 'Teléfono', 'Contacto de pagos', 'Fecha de inicio', 'Fecha de vencimiento', 'Renta mensual', 'Día de pago', 'Depósito en garantía', 'Escalación anual (%)', 'Saldo anterior a Parkos']
  const rows = [
    ['A-01', 'Manufacturas del Norte SA de CV', 'MNO010101AB1', 'pagos@manufacturas.com', '3312345678', 'Laura Pérez', '01/03/2025', '28/02/2028', '45000', '5', '90000', '5', '0'],
    ['A-02', 'Logística Bajío SA de CV', '', 'cobranza@logisticabajio.com', '', '', '15/09/2024', '14/09/2027', '38500', '10', '', '', '38500'],
  ]
  return '\uFEFF' + [headers, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n')
}